import { Capacitor } from "@capacitor/core";
import { Purchases as CapacitorPurchases } from "@revenuecat/purchases-capacitor";
import { RevenueCatUI } from "@revenuecat/purchases-capacitor-ui";
import { PAYWALL_RESULT } from "@revenuecat/purchases-typescript-internal-esm";
import { Purchases as WebPurchases } from "@revenuecat/purchases-js";
import CurrentSession from "data/userSession";
import Logger from "utils/logger";

const RC_APPLE_KEY = process.env.REACT_APP_RC_APPLE_KEY || "";
const RC_GOOGLE_KEY = process.env.REACT_APP_RC_GOOGLE_KEY || "";
const RC_WEB_KEY = process.env.REACT_APP_RC_WEB_KEY || "";
const ENTITLEMENT_ID = "KOT Supporter";

class PurchaseService {
  private isInitialized: boolean = false;
  private initializedUid: string | null = null;
  private webInstance: WebPurchases | null = null;

  async initialize(): Promise<void> {
    try {
      await CurrentSession.load();
      const uid = CurrentSession.user?.uid;
      // const uid = "T4z3qhXaUFRmVVQszYKboEEp7vE3";
      if (!uid) return;

      // Already initialized for this user
      if (this.isInitialized && this.initializedUid === uid) return;

      const displayName = CurrentSession.user?.name;

      if (Capacitor.isNativePlatform()) {
        const platform = Capacitor.getPlatform();
        const apiKey = platform === "ios" ? RC_APPLE_KEY : RC_GOOGLE_KEY;
        if (!this.isInitialized) {
          await CapacitorPurchases.configure({ apiKey });
        }
        await CapacitorPurchases.logIn({ appUserID: uid });
        if (displayName) {
          await CapacitorPurchases.setDisplayName({ displayName });
        }
      } else {
        this.webInstance = WebPurchases.configure({
          apiKey: RC_WEB_KEY,
          appUserId: uid,
        });
        if (displayName) {
          await this.webInstance.setAttributes({ $displayName: displayName });
        }
      }

      this.isInitialized = true;
      this.initializedUid = uid;
      Logger.debug(
        `[Purchases] Initialized for ${uid} on ${Capacitor.getPlatform()}`
      );
    } catch (error) {
      Logger.error("[Purchases] Failed to initialize:", error);
    }
  }

  async presentPaywall(): Promise<boolean> {
    if (!this.isInitialized) {
      Logger.error("[Purchases] Not initialized");
      return false;
    }

    if (Capacitor.isNativePlatform()) {
      return this.presentNativePaywall();
    } else {
      return this.presentWebPaywall();
    }
  }

  private async presentNativePaywall(): Promise<boolean> {
    try {
      const offerings = await CapacitorPurchases.getOfferings();
      const consumables = offerings.all["consumables"];
      if (!consumables) {
        Logger.error("[Purchases] Consumables offering not found");
        return false;
      }

      const { result } = await RevenueCatUI.presentPaywall({
        offering: consumables,
      });

      if (result === PAYWALL_RESULT.PURCHASED) {
        const { customerInfo } = await CapacitorPurchases.getCustomerInfo();
        return await this.syncSupporter(customerInfo);
      }

      return false;
    } catch (error) {
      Logger.error("[Purchases] Paywall error:", error);
      throw error;
    }
  }

  private async presentWebPaywall(): Promise<boolean> {
    if (!this.webInstance) return false;

    try {
      const offerings = await this.webInstance.getOfferings();
      const consumables = offerings.all["consumables"];
      if (!consumables) {
        Logger.error("[Purchases] Consumables offering not found");
        return false;
      }

      const { customerInfo } = await this.webInstance.presentPaywall({
        offering: consumables,
      });

      return await this.syncSupporter(customerInfo);
    } catch (error: any) {
      if (error?.code === "1" || error?.userCancelled) {
        Logger.debug("[Purchases] User cancelled");
        return false;
      }
      Logger.error("[Purchases] Purchase failed:", error);
      throw error;
    }
  }

  async checkAndSyncSupporter(): Promise<boolean> {
    try {
      if (Capacitor.isNativePlatform()) {
        const { customerInfo } = await CapacitorPurchases.getCustomerInfo();
        return await this.syncSupporter(customerInfo);
      } else if (this.webInstance) {
        const customerInfo = await this.webInstance.getCustomerInfo();
        return await this.syncSupporter(customerInfo);
      }
    } catch (error) {
      Logger.error("[Purchases] Failed to sync supporter status:", error);
    }
    return false;
  }

  async getDebugInfo(): Promise<string> {
    const lines: string[] = [];
    const uid = CurrentSession.user?.uid ?? "unknown";
    const platform = Capacitor.getPlatform();
    lines.push(`Platform: ${platform}`);
    lines.push(`User ID: ${uid}`);
    lines.push(`Initialized: ${this.isInitialized}`);
    lines.push(`Initialized UID: ${this.initializedUid}`);
    lines.push(
      `Firestore supporter: ${CurrentSession.user?.supporter ?? "N/A"}`
    );

    try {
      let customerInfo: any;
      if (Capacitor.isNativePlatform()) {
        ({ customerInfo } = await CapacitorPurchases.getCustomerInfo());
      } else if (this.webInstance) {
        customerInfo = await this.webInstance.getCustomerInfo();
      }

      if (customerInfo) {
        lines.push(
          `\nRC Original App User ID: ${
            customerInfo.originalAppUserId ?? "N/A"
          }`
        );
        lines.push(
          `RC Entitlements Active: ${JSON.stringify(
            customerInfo.entitlements?.active ?? {},
            null,
            2
          )}`
        );
        lines.push(
          `RC Entitlements All: ${JSON.stringify(
            customerInfo.entitlements?.all ?? {},
            null,
            2
          )}`
        );
        lines.push(
          `RC Non-Sub Transactions: ${JSON.stringify(
            customerInfo.nonSubscriptionTransactions ?? [],
            null,
            2
          )}`
        );
      } else {
        lines.push("\nRC Customer Info: unavailable");
      }
    } catch (error) {
      lines.push(`\nRC Error: ${error}`);
    }

    return lines.join("\n");
  }

  private async syncSupporter(customerInfo: any): Promise<boolean> {
    Logger.debug("[Purchases] Customer info:", JSON.stringify(customerInfo));
    const isActive = ENTITLEMENT_ID in (customerInfo.entitlements.active ?? {});

    if (isActive !== CurrentSession.user.supporter) {
      try {
        CurrentSession.user.supporter = isActive;
        await CurrentSession.user.save(true);
        Logger.debug(
          `[Purchases] Synced supporter status: ${
            isActive ? "granted" : "revoked"
          }`
        );
      } catch (error) {
        Logger.error("[Purchases] Failed to save supporter status:", error);
        return false;
      }
    }

    return isActive;
  }
}

const purchaseService = new PurchaseService();
export default purchaseService;
